import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { offices } from '@/constants/site'

const formatTime = (date: Date, timeZone: string): [string, string] => {
    const parts = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone }).formatToParts(date)
    const hour = parts.find(part => part.type === 'hour')?.value ?? '--'
    const minute = parts.find(part => part.type === 'minute')?.value ?? '--'

    return [hour, minute]
}

interface OfficeClocksProps {
    className?: string
    layout?: 'inline' | 'stack'
}

// Live local time in each office. Rendered after mount so server and client
// markup always match.
export const OfficeClocks: FC<OfficeClocksProps> = ({ className, layout = 'inline' }): ReactNode => {
    const [now, setNow] = useState<Date | null>(null)

    useEffect(() => {
        setNow(new Date())

        const timer = window.setInterval(() => setNow(new Date()), 10000)

        return () => {
            window.clearInterval(timer)
        }
    }, [])

    return (
        <ul className={cn(layout === 'inline' ? 'flex items-center gap-6' : 'flex flex-col gap-3', 't-label', className)}>
            {offices.map(office => {
                const [hour, minute] = now ? formatTime(now, office.timeZone) : ['--', '--']

                return (
                    <li key={office.short} className={cn('flex items-center gap-2.5', layout === 'stack' && 'justify-between gap-8')}>
                        <span className='text-tone-muted'>
                            {layout === 'stack' ? office.city : office.short}
                        </span>
                        <span className='tabular-nums'>
                            {hour}
                            <span className='animate-[blink_2s_steps(1)_infinite]'>
                                :
                            </span>
                            {minute}
                        </span>
                    </li>
                )
            })}
        </ul>
    )
}
